import React from "react";
import { Check } from "lucide-react";

const ProgressIndicator = ({ currentStep, flowType }) => {
  const insuranceSteps = [
    { id: "survey", label: "Survey" },
    {
      id: "contact-info",
      label: "Insurance Profile",
      matchSteps: ["contact-info"],
    },
    {
      id: "post-auth-survey",
      label: "Insurance Profile",
      matchSteps: ["post-auth-survey"],
    },
    {
      id: "documents",
      label: "Documents",
      matchSteps: ["insurance-options", "document", "canopy"],
    },
  ];

  const insuranceRenewalSteps = [
    {
      id: "policies",
      label: "Insurance Profile",
      matchSteps: ["policies"],
    },
    {
      id: "renewal-survey",
      label: "Insurance Profile",
      matchSteps: ["renewal-survey"],
    },
    {
      id: "contact-info",
      label: "Insurance Profile",
      matchSteps: ["contact-info"],
    },
  ];

  // Extended warranty steps (new)
  const extendedWarrantySteps = [
    { id: "survey", label: "Survey" },
    {
      id: "contact-info",
      label: "Insurance Profile",
      matchSteps: ["contact-info"],
    },
    {
      id: "vehicle-selection",
      label: "Vehicle Selection",
      matchSteps: ["vehicle-selection", "extended-warranty-policy"],
    },
    {
      id: "vehicle-completion",
      label: "Complete",
      matchSteps: ["vehicle-completion", "success"],
    },
  ];

  const refinancingSteps = [
    { id: "survey", label: "Survey" },
    {
      id: "contact-info",
      label: "Insurance Profile",
      matchSteps: ["contact-info"],
    },
    {
      id: "vehicle-selection",
      label: "Vehicle Selection",
      matchSteps: [
        "refinancing-vehicle-selection",
        "refinancing-upload-policy",
      ],
    },
    {
      id: "vehicle-completion",
      label: "Complete",
      matchSteps: ["refinancing-vehicle-completion", "success"],
    },
  ];

  let steps;

  if (flowType === "extended-warranty") {
    steps = extendedWarrantySteps;
  } else if (flowType === "insurance-renewal") {
    steps = insuranceRenewalSteps;
  } else if (flowType === "refinancing") {
    steps = refinancingSteps;
  } else {
    steps = insuranceSteps;
  }

  // Find the step object for the current step
  // Check both direct ID matches and steps within the matchSteps array
  const findCurrentStepObj = () => {
    // First check for a direct match
    const directMatch = steps.find((s) => s.id === currentStep);
    if (directMatch) return directMatch;

    // Then check for matches in the matchSteps arrays
    return steps.find(
      (s) => s.matchSteps && s.matchSteps.includes(currentStep)
    );
  };

  const currentStepObj = findCurrentStepObj();

  // Find the index of the current step
  const currentIndex = currentStepObj ? steps.indexOf(currentStepObj) : 0;

  // For smaller screens, calculate which steps to show
  const getPrevStepIndex = () => Math.max(0, currentIndex - 1);
  const getNextStepIndex = () => Math.min(steps.length - 1, currentIndex + 1);

  // If current is first step, show first three steps
  // If current is last step, show last three steps
  // Otherwise show prev, current, next
  const getMobileStepsToShow = () => {
    if (currentIndex === 0) return [0, 1, 2];
    if (currentIndex === steps.length - 1) {
      return [steps.length - 3, steps.length - 2, steps.length - 1];
    }
    return [getPrevStepIndex(), currentIndex, getNextStepIndex()];
  };

  const mobileSteps = getMobileStepsToShow();

  return (
    <div className="w-full max-w-md mx-auto p-4 pt-6">
      {/* Desktop View - show all steps */}
      <div className="flex items-center justify-between w-full">
        {steps.map((s, index) => {
          const isCompleted = index < currentIndex;
          const isActive = index === currentIndex;
          const isUpcoming = index > currentIndex;

          return (
            <React.Fragment key={s.id}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-[24px] h-[24px] flex items-center justify-center rounded-full ${
                    isCompleted ? "bg-green-100 border-green-300" : ""
                  }${isActive ? "bg-green-100" : ""}${
                    isUpcoming ? "border-gray-300 bg-gray-100" : ""
                  } transition-colors`}
                >
                  {isCompleted ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <span
                      className={`text-sm font-medium ${
                        isActive ? "text-green-600" : ""
                      } ${isUpcoming ? "text-gray-400" : ""}`}
                    >
                      {index + 1}
                    </span>
                  )}
                </div>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`flex-1 h-[1px] ${
                    index < currentIndex - 1
                      ? "bg-green-200"
                      : index === currentIndex - 1
                      ? "bg-green-200"
                      : "bg-gray-200"
                  } transition-colors`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
      {/* Mobile View - only show prev, current, and next steps */}
      {/* <div className="md:hidden flex justify-between items-center mx-4 my-1">
        {mobileSteps.map((index, i) => {
          const step = steps[index];
          if (!step) return null;

          const isCompleted = index < currentIndex;
          const isActive = index === currentIndex;
          const isUpcoming = index > currentIndex;

          return (
            <div
              key={step.id}
              className="flex flex-col items-center justify-start"
              style={{
                flex: "1",
                maxWidth: isActive ? "40%" : "30%",
              }}
            >
              <div className="flex flex-col items-center">

                {i === 0 && index !== 0 && (
                  <div className="text-gray-400 text-xs h-4">←</div>
                )}
                {!(i === 0 && index !== 0) && <div className="h-4"></div>}


                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    isCompleted
                      ? "bg-green-100"
                      : isActive
                      ? "bg-blue-100"
                      : "bg-gray-100"
                  }`}
                >
                  {isCompleted ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <span className="text-sm">{index + 1}</span>
                  )}
                </div>


                <div className="h-12 flex items-start justify-center pt-1">
                  <span
                    className={`text-xs ${
                      isUpcoming
                        ? "text-gray-400"
                        : isActive
                        ? "text-blue-600 font-medium"
                        : "text-green-600"
                    } text-center max-w-full truncate`}
                  >
                    {step.label}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div> */}
      {/* Progress bar */}
      {/* <div className="mt-2 relative h-1 bg-gray-200 rounded-full mx-4">
        <div
          className="absolute top-0 left-0 h-1 bg-green-500 rounded-full transition-all duration-300 ease-out"
          style={{
            width: `${Math.min(
              100,
              (currentIndex / (steps.length - 1)) * 100
            )}%`,
          }}
        />
      </div> */}
      {/* Mobile Step Count */}
      {/* <div className="md:hidden text-xs text-center text-gray-500 mt-1">
        Step {currentIndex + 1} of {steps.length}
      </div> */}
    </div>
  );
};

export default ProgressIndicator;
